// The composer, pinned to the bottom of the chat column (UI Core AI chat).
// One control boundary around the textarea and the send button in its own
// lane beside it (never on the text). One row tall when empty; the textarea
// grows with its content up to eight lines, then scrolls inside itself. The
// keyboard hint is the textarea's description (aria-describedby, hidden),
// not a visible line; the meta row appears only for the counter or the
// over-limit message.
//   Enter sends, Shift+Enter starts a new line, an IME composition never sends.
//   Kirim is disabled while the text is empty, over the limit, or a turn runs.
//   The counter appears from 3600 characters and names the limit when over.
// Attachments (phase 2, optional): the paperclip sits in the leading lane,
// the picked files show as chips above the text, a file dropped on the box or
// an image pasted into the text joins them. Kirim waits for uploads; with
// ready files it may send with an empty text (the screen adds a default line).
import { useEffect, useId, useLayoutEffect, useRef, useState, type ClipboardEvent, type DragEvent, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { formatCount } from "../lib/format";
import { Notice } from "../ui/components";
import { Glyph } from "../ui/icons";
import { ComposerChips } from "./AttachmentChips";
import { AttachmentPicker } from "./AttachmentPicker";
import type { Attachments } from "./attachments";
import { COUNTER_FROM, MESSAGE_MAX } from "./support";

export interface ComposerProps {
  value: string;
  onChange: (next: string) => void;
  onSend: (text: string) => void;
  /** A turn is running: typing stays open, sending waits. */
  busy: boolean;
  /** An optional 44px control before Kirim (the Pertanyaan umum button). */
  lead?: ReactNode;
  /** Locked (the guest quota is used up): nothing can be typed or sent. */
  disabled?: boolean;
  placeholder?: string;
  /** The attachment tray (useAttachments); no paperclip without it. */
  attachments?: Attachments;
}

const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");

/** The textarea's height at one line (44px control, 24px line, 10px padding). */
const SINGLE_LINE_MAX = 46;

export function Composer({ value, onChange, onSend, busy, lead, disabled = false, placeholder = "Mau tanya apa?", attachments }: ComposerProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const id = useId();
  const length = value.length;
  const over = length > MESSAGE_MAX;
  const files = attachments?.ready.length ?? 0;
  const uploading = attachments?.pending ?? false;
  const empty = value.trim().length === 0 && files === 0;
  const canSend = !empty && !over && !busy && !disabled && !uploading;
  const [drag, setDrag] = useState(false);

  // One line sits beside the actions; once the text wraps, the layout moves
  // the actions below it (data-multi) and stays there until the text is sent,
  // so the width change can never flip it back and forth.
  const [multi, setMulti] = useState(false);
  const previous = useRef(value);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    const h = el.scrollHeight;
    if (h > 0) el.style.height = `${h}px`;
    if (!multi && h > SINGLE_LINE_MAX) setMulti(true);
  }, [value, multi]);
  useEffect(() => {
    if (previous.current !== "" && value === "") setMulti(false);
    previous.current = value;
  }, [value]);

  const submit = () => {
    if (!canSend) return;
    onSend(value.trim());
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    submit();
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit();
  };

  const canAttach = !!attachments && !disabled;
  const onDragOver = (e: DragEvent) => {
    if (!canAttach || !hasFiles(e)) return;
    e.preventDefault();
    if (!drag) setDrag(true);
  };
  const onDrop = (e: DragEvent) => {
    if (!canAttach || !hasFiles(e)) return;
    e.preventDefault();
    setDrag(false);
    attachments.add(Array.from(e.dataTransfer.files ?? []));
  };
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!canAttach) return;
    const images = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith("image/"));
    if (images.length === 0) return;
    e.preventDefault();
    attachments.add(images);
  };
  const items = attachments?.items ?? [];

  return (
    <form className="composer" onSubmit={onSubmit} aria-label="Tanya Pluto">
      <div
        className="composer-box"
        data-invalid={over ? "" : undefined}
        data-multi={multi ? "" : undefined}
        data-disabled={disabled ? "" : undefined}
        data-attach={attachments ? "" : undefined}
        data-drag={drag ? "" : undefined}
        onDragOver={onDragOver}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
      >
        {attachments ? <ComposerChips items={items} onRemove={attachments.remove} disabled={disabled} /> : null}
        {attachments ? <AttachmentPicker onFiles={attachments.add} disabled={disabled || attachments.room === 0} multiple={!attachments.guest} /> : null}
        <textarea
          id={`${id}-q`}
          ref={ref}
          className="composer-input"
          rows={1}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          aria-label="Pertanyaan untuk Pluto"
          aria-describedby={over ? `${id}-err ${id}-hint` : `${id}-hint`}
          aria-invalid={over ? true : undefined}
          onChange={(e) => onChange(e.currentTarget.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
        <div className="composer-actions">
          {lead}
          <button type="submit" className="composer-send" aria-label="Kirim" title={uploading ? "Menunggu unggahan selesai" : "Kirim"} disabled={!canSend}>
            <Glyph name="send" size={20} />
          </button>
        </div>
        {over || length >= COUNTER_FROM ? (
          <div className="composer-meta">
            {over ? (
              <p className="composer-hint" id={`${id}-err`} data-state="error" role="alert">
                <Glyph name="danger" size={16} />
                <span>Maksimal {formatCount(MESSAGE_MAX)} karakter. Persingkat pertanyaan Anda.</span>
              </p>
            ) : null}
            {length >= COUNTER_FROM ? (
              <span className="composer-count num" data-over={over ? "" : undefined} aria-label={`${formatCount(length)} dari ${formatCount(MESSAGE_MAX)} karakter`}>
                {formatCount(length)}/{formatCount(MESSAGE_MAX)}
              </span>
            ) : null}
          </div>
        ) : null}
        <span id={`${id}-hint`} hidden>
          Enter untuk kirim, Shift+Enter untuk baris baru
        </span>
      </div>
      {attachments ? <ComposerNotes attachments={attachments} /> : null}
    </form>
  );
}

/** Under the box: the count note, the guest line, the Drive notice. */
function ComposerNotes({ attachments }: { attachments: Attachments }) {
  const { note, guest, items, driveOff } = attachments;
  const guestLine = guest && items.length > 0;
  if (!note && !guestLine && !driveOff) return null;
  return (
    <div className="composer-notes">
      {note ? (
        <p className="composer-hint" data-state="error" role="alert">
          <Glyph name="danger" size={16} />
          <span>{note}</span>
        </p>
      ) : null}
      {guestLine ? (
        <p className="composer-hint">
          <Glyph name="infoCircle" size={16} />
          <span>Lampiran tamu tidak disimpan.</span>
        </p>
      ) : null}
      {driveOff ? (
        <Notice tone="warning" title="Lampiran belum tersedia" className="composer-notice">
          <p>Penyimpanan file belum tersedia. Hubungi admin. Pesan teks tetap dapat dikirim.</p>
        </Notice>
      ) : null}
    </div>
  );
}
